import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ProfileHeader from './components/ProfileHeader';
import BasicInfoTab from './components/BasicInfoTab';
import MusicalSpecialtiesTab from './components/MusicalSpecialtiesTab';
import BusinessVerificationTab from './components/BusinessVerificationTab';
import AccountPreferencesTab from './components/AccountPreferencesTab';
import PerformanceHistoryTab from './components/PerformanceHistoryTab';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const PartnerProfile = () => {
  const { user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('basic');
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [language, setLanguage] = useState('fr');
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const tabs = [
    { id: 'basic', label: 'Informations de Base', icon: 'User', component: BasicInfoTab },
    { id: 'specialties', label: 'Spécialités Musicales', icon: 'Music', component: MusicalSpecialtiesTab },
    { id: 'business', label: 'Vérification Business', icon: 'Building', component: BusinessVerificationTab },
    { id: 'preferences', label: 'Préférences', icon: 'Settings', component: AccountPreferencesTab },
    { id: 'performance', label: 'Historique', icon: 'BarChart3', component: PerformanceHistoryTab }
  ];

  const calculateCompletionPercentage = (profileData) => {
    const fields = [
      profileData?.name,
      profileData?.email,
      profileData?.phone,
      profileData?.role,
      profileData?.bio,
      profileData?.genres?.length > 0,
      profileData?.equipment?.length > 0,
      profileData?.businessName,
      profileData?.paymentMethod,
      profileData?.portfolioFiles?.length > 0
    ];
    const completedFields = fields?.filter(field => field)?.length;
    return Math.round((completedFields / fields?.length) * 100) || 0;
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) setLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: userProfile, error: userErr } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        if (userErr && userErr.code !== 'PGRST116') throw userErr;
        const { data: partner, error: partnerErr } = await supabase
          .from('partners')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        if (partnerErr && partnerErr.code !== 'PGRST116') throw partnerErr;
        const composed = {
          id: userProfile?.id || user.id,
          name: userProfile?.full_name || '',
          email: userProfile?.email || user?.email,
          phone: userProfile?.phone || '',
          role: userProfile?.role || partner?.partner_type || '',
          experience: userProfile?.experience || '',
          location: userProfile?.location || partner?.city || '',
          website: userProfile?.website || '',
          bio: userProfile?.bio || '',
          languages: userProfile?.languages || [],
          avatar: userProfile?.avatar_url || '',
          memberSince: userProfile?.created_at ? new Date(userProfile.created_at).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : '',
          genres: userProfile?.genres || [],
          equipment: userProfile?.equipment || [],
          businessName: partner?.business_name || '',
          paymentMethod: partner?.payment_method || '',
          portfolioFiles: [],
          certifications: []
        };
        setProfile(composed);
      } catch (e) {
        console.error('Load profile error', e);
        setError('Impossible de charger le profil.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const handleSaveProfile = async (tabData) => {
    if (!user) return;
    setSaving(true);
    setError(null);
    setFeedback(null);
    try {
      const updateUserProfile = {
        full_name: tabData?.name ?? profile?.name ?? '',
        phone: tabData?.phone ?? null,
        location: tabData?.location ?? null,
        website: tabData?.website ?? null,
        bio: tabData?.bio ?? null,
        experience: tabData?.experience ?? null,
        languages: tabData?.languages ?? [],
        role: tabData?.role ?? profile?.role ?? null
      };
      Object.keys(updateUserProfile).forEach(k => updateUserProfile[k] === undefined && (updateUserProfile[k] = null));
      const { error: upErr } = await supabase
        .from('user_profiles')
        .upsert({ id: user.id, ...updateUserProfile }, { onConflict: 'id' });
      if (upErr) throw upErr;
      if (tabData?.businessName) {
        const partnerPayload = {
          business_name: tabData.businessName,
          user_id: user.id,
          partner_type: tabData?.role || profile?.role || null
        };
        const { error: partnerErr } = await supabase
          .from('partners')
          .upsert(partnerPayload, { onConflict: 'user_id' });
        if (partnerErr) console.warn('Partner upsert warning', partnerErr);
      }
      setProfile(prev => ({ ...prev, ...tabData }));
      setFeedback('Modifications sauvegardées.');
    } catch (e) {
      console.error('Save profile error', e);
      setError('Échec de la sauvegarde.');
      throw e;
    } finally {
      setSaving(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const handleEditPhoto = () => {
    if (!user) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e?.target?.files?.[0];
      if (!file) return;
      setSaving(true);
      setError(null);
      setFeedback(null);
      try {
        const ext = file.name.split('.').pop();
        const path = `${user.id}/avatar_${Date.now()}.${ext}`;
        const { error: uploadErr } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });
        if (uploadErr) throw uploadErr;
        const { data: publicUrlData } = supabase.storage.from('avatars').getPublicUrl(path);
        const avatar_url = publicUrlData?.publicUrl;
        const { error: updateErr } = await supabase.from('user_profiles').update({ avatar_url }).eq('id', user.id);
        if (updateErr) throw updateErr;
        setProfile(prev => ({ ...prev, avatar: avatar_url }));
        setFeedback('Photo mise à jour.');
      } catch (e2) {
        console.error('Avatar upload error', e2);
        setError("Échec de l'upload de la photo.");
      } finally {
        setSaving(false);
        setTimeout(() => setFeedback(null), 4000);
      }
    };
    input.click();
  };

  const ActiveTabComponent = tabs?.find(tab => tab?.id === activeTab)?.component;
  const completionPercentage = calculateCompletionPercentage(profile);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Veuillez vous connecter pour accéder au profil.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
            <span className="text-muted-foreground">Chargement du profil...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Profil Partenaire - {profile?.name} | Artis.io</title>
        <meta name="description" content="Gérez votre profil partenaire, vos spécialités musicales et vos préférences sur Artis.io" />
      </Helmet>
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {(error || feedback || saving) && (
            <div className="mb-6 space-y-2">
              {error && <div className="px-4 py-3 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-600">{error}</div>}
              {feedback && !error && <div className="px-4 py-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-600">{feedback}</div>}
              {saving && <div className="px-4 py-3 rounded-md bg-muted border border-border text-sm flex items-center gap-2"><span className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin"></span>Sauvegarde en cours...</div>}
            </div>
          )}
          <ProfileHeader profile={profile} completionPercentage={completionPercentage} onEditPhoto={handleEditPhoto} />
          <div className="bg-surface border border-border rounded-lg mb-6">
            <div className="border-b border-border">
              <nav className="flex overflow-x-auto">
                {tabs?.map((tab) => (
                  <button key={tab?.id} onClick={() => setActiveTab(tab?.id)} className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors duration-150 ${activeTab === tab?.id ? 'border-accent text-accent bg-accent/5' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'}`}>
                    <Icon name={tab?.icon} size={16} />
                    <span className="hidden sm:inline">{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
            <div className="p-6">
              {ActiveTabComponent && <ActiveTabComponent profile={profile} onSave={handleSaveProfile} saving={saving} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerProfile;
